import { useEffect, useState } from "react";
import { useOfficeContext } from "../../../shared/hooks/useOfficeContext";
import { useWordDocument } from "../../document/hooks/useWordDocument";
import { useMailboxItem, readAttachmentBase64 } from "../../email/hooks/useMailboxItem";
import { getActivePresentationBytes } from "../../deckorate/features/ColorTools/services/pptxBytes";
import { PPTX_MIME_TYPE } from "../../deckorate/utils/officePptx";
import {
  getActiveDocumentBase64,
  DOCX_MIME_TYPE,
} from "../../../../integrations/tools/word/services/wordFile";
import { bufferToBase64 } from "../../../shared/base64";

export type EngageDocHost = "word" | "powerpoint" | "outlook";

export interface EngageDocAttachment {
  readonly id: string;
  readonly name: string; // filename shown and sent to Engage
  readonly caption: string; // right-hand label (e.g. "Word document", "12 KB")
  /**
   * Legacy text-export path: exposes plain text that is base64-encoded downstream
   * before upload. No longer produced for Word/PowerPoint — both now attach the
   * original file via `getBytes` (AIVA-7885) — but kept for any text-only source.
   */
  readonly getText?: () => Promise<string>;
  /**
   * Binary attachments expose their raw bytes as base64, resolving to `null` when
   * the bytes can't be read. Used by the Word document (.docx) and PowerPoint
   * presentation (.pptx) via Office.js `getFileAsync` (AIVA-7885), and by Outlook
   * file attachments via `getAttachmentContentAsync` (AIVA-6989). `mimeType` is
   * the attachment's content type.
   */
  readonly getBytes?: () => Promise<string | null>;
  readonly mimeType?: string;
}

export interface EngageDocContext {
  readonly ready: boolean;
  readonly host: EngageDocHost | null;
  readonly primary: string; // doc/presentation name, or email sender
  readonly secondary: string; // "{n} words" / "{n} slides" / email subject
  readonly caption: string; // "…text will be attached" / attachment count
  readonly suggestedTitle: string;
  readonly attachments: EngageDocAttachment[];
  readonly empty: boolean;
  readonly emptyMessage: string;
  // Outlook only: the open email's Graph REST id, used to export it as a .eml
  // to the job's OneDrive folder on submit (AIVA-6990).
  readonly emailId?: string;
}

const UNTITLED_DOCUMENT = "Untitled Document";
const UNTITLED_PRESENTATION = "Untitled Presentation";

const EMPTY: EngageDocContext = {
  ready: false,
  host: null,
  primary: "",
  secondary: "",
  caption: "",
  suggestedTitle: "",
  attachments: [],
  empty: true,
  emptyMessage: "",
};

function stripExt(name: string): string {
  return name.replace(/\.(docx?|pptx?)$/i, "").trim();
}

function basename(url: string, fallback: string): string {
  if (!url) return fallback;
  const seg = url.split(/[\\/]/).pop() ?? "";
  try {
    return decodeURIComponent(seg) || fallback;
  } catch {
    return seg || fallback;
  }
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1_048_576) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1_048_576).toFixed(1)} MB`;
}

function plural(n: number, singular: string): string {
  return `${n} ${singular}${n === 1 ? "" : "s"}`;
}

type WordDoc = ReturnType<typeof useWordDocument>;
type MailItem = ReturnType<typeof useMailboxItem>;

function buildWordContext(word: WordDoc): EngageDocContext {
  const name = word.fullDoc.name || UNTITLED_DOCUMENT;
  const words = word.fullDoc.wordCount ?? 0;
  const empty = name === UNTITLED_DOCUMENT && words === 0;
  const base = stripExt(name);
  return {
    ready: true,
    host: "word",
    primary: name,
    secondary: `${words.toLocaleString()} words`,
    caption: "Document will be attached",
    suggestedTitle: base,
    empty,
    emptyMessage: "Open a document in Word to create a workflow job",
    // Attach the original Word document (.docx binary) rather than a plain-text
    // export of its body, so the job carries the real file (AIVA-7885). The bytes
    // ride the same base64 attachment path as Outlook file attachments via
    // getBytes; getActiveDocumentBase64 reads the last-saved file, so resolve to
    // null on failure and the attachment is simply skipped.
    attachments: empty
      ? []
      : [
          {
            id: "word-doc",
            name: `${base}.docx`,
            caption: "Word document",
            mimeType: DOCX_MIME_TYPE,
            getBytes: async () => {
              try {
                return await getActiveDocumentBase64();
              } catch {
                return null;
              }
            },
          },
        ],
  };
}

function buildPptContext(pptName: string, pptSlides: number): EngageDocContext {
  const name = pptName || UNTITLED_PRESENTATION;
  const empty = name === UNTITLED_PRESENTATION && pptSlides === 0;
  const base = stripExt(name);
  return {
    ready: true,
    host: "powerpoint",
    primary: name,
    secondary: plural(pptSlides, "slide"),
    caption: "Presentation will be attached",
    suggestedTitle: base,
    empty,
    emptyMessage: "Open a presentation in PowerPoint to create a workflow job",
    // Attach the original presentation (.pptx binary) rather than a plain-text
    // export of its slides, so the job carries the real file (AIVA-7885). The
    // bytes ride the same base64 getBytes path as the Word document and Outlook
    // attachments; getActivePresentationBytes reads the last-saved file, so
    // resolve to null on failure and the attachment is simply skipped.
    attachments: empty
      ? []
      : [
          {
            id: "ppt-doc",
            name: `${base}.pptx`,
            caption: "Presentation",
            mimeType: PPTX_MIME_TYPE,
            getBytes: async () => {
              try {
                return bufferToBase64(await getActivePresentationBytes());
              } catch {
                return null;
              }
            },
          },
        ],
  };
}

function buildOutlookContext(mail: MailItem): EngageDocContext {
  const hasItem = !!mail.itemId || mail.subject.length > 0;
  // Exclude only inline IMAGES (embedded body pictures / signature logos).
  // Inline *files* — e.g. a Word doc dropped into the message body, which
  // Outlook shows as a link — are real attachments and must be surfaced.
  const realAtts = mail.attachments.filter(
    (a) => !(a.isInline && (a.contentType ?? "").toLowerCase().startsWith("image/")),
  );
  return {
    ready: true,
    host: "outlook",
    primary: mail.from || "No sender",
    secondary: mail.subject || "No subject",
    caption: realAtts.length > 0 ? plural(realAtts.length, "attachment") : "No attachments",
    suggestedTitle: mail.subject || "",
    empty: !hasItem,
    emptyMessage: "Open an email in Outlook to create a workflow job",
    emailId: mail.itemId ?? undefined,
    // Each email file attachment exposes its bytes via getBytes so it can be
    // uploaded to the job and shown in the job's details (AIVA-6989).
    attachments: realAtts.map((a) => ({
      id: a.id,
      name: a.name,
      caption: formatSize(a.size),
      mimeType: a.contentType || "application/octet-stream",
      getBytes: () => readAttachmentBase64(a.rawId ?? ""),
    })),
  };
}

/**
 * Host-aware document context for the Engage "Create Job" panel — mirrors the
 * prototype's per-host card + attachment rows. Word/PowerPoint expose the
 * document text as a `.txt` attachment; Outlook surfaces the email + its file
 * attachments, whose bytes upload via Office.js `getAttachmentContentAsync`.
 */
export function useEngageDocument(): EngageDocContext {
  const office = useOfficeContext();
  const isWord = office.isReady && office.isWord;
  const isPpt = office.isReady && office.isPowerPoint;
  const isOutlook = office.isReady && office.isOutlook;

  const word = useWordDocument(isWord);
  const mail = useMailboxItem();

  const [pptName, setPptName] = useState(UNTITLED_PRESENTATION);
  const [pptSlides, setPptSlides] = useState(0);

  useEffect(() => {
    if (!isPpt) return undefined;
    let alive = true;
    void (async () => {
      try {
        const url = globalThis.Office?.context?.document?.url ?? "";
        const name = basename(url, UNTITLED_PRESENTATION);
        let count = 0;
        await PowerPoint.run(async (ctx) => {
          const c = ctx.presentation.slides.getCount();
          await ctx.sync();
          count = c.value;
        });
        if (alive) {
          setPptName(name);
          setPptSlides(count);
        }
      } catch {
        // Leave defaults — the empty-state prompt will show.
      }
    })();
    return () => {
      alive = false;
    };
  }, [isPpt]);

  if (!office.isReady) return EMPTY;
  if (isWord) return buildWordContext(word);
  if (isPpt) return buildPptContext(pptName, pptSlides);
  if (isOutlook) return buildOutlookContext(mail);
  return EMPTY;
}
